import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import throttle from 'lodash/throttle';
import { useEffect } from 'react';

import { getAuthorizationToken } from '@/api/setup/authentication';
import { userPermissionsAtom, userRolesAtom } from '@/atoms/auth';
import { resolveAuthState } from '@/lib/auth/resolveAuthState';
import { areSetsEqual, toSet } from '@/lib/set-utils';
import { authKeys } from '@/utils/queryKeys';

import { useSettings } from './useSettings';

import { log } from '@/lib/utils';
import type { Permission, Role } from '@/types/auth';
import * as Sentry from '@sentry/nextjs';

/*
 * Only allow refresh every second, this is to prevent duplicate requesting
 * in the token expirating and the user being logged out.
 */
const throttledGetAuthorizationToken = throttle(getAuthorizationToken, 1000, {
  trailing: true,
});

/**
 * Custom hook that manages user authentication tokens and syncs roles/permissions with Jotai atoms.
 *
 * This hook handles the complex authentication state management between multiple data sources:
 * - getAuthorizationToken API (primary source when user is authenticated)
 * - useSettings userSessionData (fallback source)
 * - Automatic cleanup when user becomes unauthenticated
 *
 * Key features:
 * - Prevents limbo state by prioritizing settings authentication status over token data
 * - Automatically invalidates auth queries when user becomes unauthenticated
 * - Syncs roles/permissions to Jotai atoms for global state access
 * - Throttled token fetching to prevent excessive API calls during auth transitions
 *
 * @returns Object containing auth token fetching state and data
 */
export const useAuthToken = () => {
  const queryClient = useQueryClient();
  const { settings, userSessionData } = useSettings();
  const setPermissions = useSetAtom(userPermissionsAtom);
  const setRoles = useSetAtom(userRolesAtom);
  const currentPermissions = useAtomValue(userPermissionsAtom);
  const currentRoles = useAtomValue(userRolesAtom);

  const isQueryEnabled = !!(
    settings?.user?.is_authenticated && settings?.user?.id
  );

  const { isFetching, data, refetch, error } = useQuery({
    queryKey: authKeys.token(settings?.auth_token, {
      isAuthenticated: settings?.user?.is_authenticated,
      userId: settings?.user?.id,
    }),
    queryFn: () => throttledGetAuthorizationToken(),
    refetchInterval: 240_000, // 4 minutes
    refetchIntervalInBackground: true,
    // Disable query when user is not authenticated
    enabled: isQueryEnabled,
  });

  // SENTRY: Monitor auth token query failures for authenticated users
  useEffect(() => {
    if (error && isQueryEnabled) {
      Sentry.captureException(error, {
        tags: {
          auth_issue: 'auth_token_query_failed',
          component: 'useAuthToken',
        },
        extra: {
          userId: settings?.user?.id,
          userEmail: settings?.user?.email,
          isAuthenticated: settings?.user?.is_authenticated,
          hasAuthToken: !!settings?.auth_token,
        },
      });
    }
  }, [
    error,
    isQueryEnabled,
    settings?.user?.id,
    settings?.user?.email,
    settings?.user?.is_authenticated,
    settings?.auth_token,
  ]);

  /**
   * Main effect that handles role/permission updates from multiple auth data sources.
   * Delegates decision logic to resolveAuthState() and handles atom writes + monitoring.
   */
  useEffect(() => {
    const isUserAuthenticated = !!(
      settings?.user?.is_authenticated && settings?.user?.id
    );

    const resolved = resolveAuthState({
      settingsAuthenticated: isUserAuthenticated,
      tokenPermissions: data?.permissions,
      tokenRoles: data?.role,
      sessionPermissions: userSessionData?.permissions,
      sessionRoles: userSessionData?.role,
    });

    log(`🔄 [useAuthToken] Resolved auth state (source: ${resolved.source}):`, {
      permissions: resolved.permissions,
      roles: resolved.roles,
    });

    // Skip updates only during the transient loading window where auth sources
    // have not resolved yet. Once they resolve (even to null), allow the
    // default state to apply so stale roles/permissions are cleared correctly.
    const isTokenAuthStatePending = typeof data === 'undefined';
    const isSessionAuthStatePending = typeof userSessionData === 'undefined';

    if (
      resolved.source === 'default' &&
      isUserAuthenticated &&
      (isTokenAuthStatePending || isSessionAuthStatePending)
    ) {
      return;
    }

    const newPermissions = toSet<Permission>(resolved.permissions);
    const newRoles = toSet<Role>(resolved.roles);

    if (!areSetsEqual(newPermissions, currentPermissions)) {
      log('🔑 [useAuthToken] Updating permissions:', {
        old: Array.from(currentPermissions),
        new: Array.from(newPermissions),
      });
      setPermissions(newPermissions);
    }

    if (!areSetsEqual(newRoles, currentRoles)) {
      // Log role downgrades for authenticated users
      if (isUserAuthenticated && currentRoles.size > newRoles.size) {
        const lostRoles = Array.from(currentRoles).filter(
          (role) => !newRoles.has(role)
        );
        if (
          lostRoles.some((role) =>
            ['Essentials', 'Creator', 'Pro', 'Premium', 'Business'].includes(
              role
            )
          )
        ) {
          Sentry.captureMessage(
            'Role Downgrade: Authenticated user lost premium roles unexpectedly',
            {
              level: 'warning',
              tags: {
                auth_issue: 'role_downgrade',
                component: 'useAuthToken',
              },
              extra: {
                previousRoles: Array.from(currentRoles),
                newRoles: Array.from(newRoles),
                lostRoles,
                source: resolved.source,
                settingsUserId: settings?.user?.id,
                userEmail: settings?.user?.email,
              },
            }
          );
        }
      }

      log('👤 [useAuthToken] Updating roles:', {
        old: Array.from(currentRoles),
        new: Array.from(newRoles),
      });
      setRoles(newRoles);
    }
  }, [
    data,
    userSessionData,
    setPermissions,
    setRoles,
    currentPermissions,
    currentRoles,
    settings,
  ]);

  /**
   * Effect that invalidates auth token queries when user becomes unauthenticated.
   * This prevents stale token data from being cached and reused inappropriately.
   */
  useEffect(() => {
    const isUserAuthenticated =
      settings?.user?.is_authenticated && settings?.user?.id;

    // If user becomes unauthenticated, invalidate all auth token queries to prevent stale data
    if (
      !isUserAuthenticated &&
      settings?.user?.is_authenticated !== undefined
    ) {
      log(
        '🗑️ [useAuthToken] User authentication state changed - invalidating auth token queries:',
        {
          isAuthenticated: settings?.user?.is_authenticated,
          userId: settings?.user?.id,
        }
      );

      queryClient.invalidateQueries({
        queryKey: ['auth'], // Invalidate all auth queries
        refetchType: 'none', // Don't refetch since user is not authenticated
      });
    }
  }, [settings?.user?.is_authenticated, settings?.user?.id, queryClient]);

  return {
    isAuthTokenFetching: isFetching,
    authTokenData: data,
    refetchAuthToken: refetch,
  };
};
